<template>
  <div>
    <div class="title">{{ i18n('我嘅邀請記錄') }}</div>
    <div class="invite-list">
      <div class="invite-list-tab">
        <NavTabs spaceAround="true" :list="navTabs" @change="navChange"></NavTabs>
      </div>
      <div class="invite-user-list">
        <div
          v-for="(item, index) in friendsInfoList"
          :key="index"
          :class="['invite-user-list-inner', { empty: index == tabId && item.length === 0 }]"
        >
          <div v-show="index == tabId" class="user-list">
            <!-- 奖赏在路上顶部提示 -->
            <div class="top-tips" v-if="tabId === NavTabId.ing && item.length > 0">
              <AnnouncementIcon class="icon" color="#FA9D3B" width="32" height="32"></AnnouncementIcon>
              <div class="text">{{ i18n('合資格好友成功使用內地迎新禮包內任一優惠券後，你將獲獎賞') }}</div>
            </div>
            <div class="success-empty" v-if="item.length === 0">
              <template v-if="isFinishInvite && [NavTabId.ing, NavTabId.failed].includes(tabId)">
                <div class="tips">{{ i18n('你已獲得全部獎賞') }}</div>
              </template>
              <template v-else>
                <div
                  class="tips"
                  v-html="i18n('邀請合資格好友<br/>你有機會獲得高達HK$X獎賞', { sum: allCouponDiscount })"
                ></div>
                <FitButton class="btn" @click="onShare" v-stat.brow.click="{ tag: 'c2c.invite_list_btn' }">
                  {{ i18n('立即邀請') }}
                </FitButton>
              </template>
            </div>
            <div v-for="(user, idx) in item" :key="idx" class="user-item">
              <div class="user-item-left">
                <img v-if="user.faceUrl && user.faceUrl !== 'none'" class="face-url" :src="user.faceUrl" />
                <div v-else class="face-url face-url-bg"></div>
                <div class="nick-name">{{ user.nickName }}</div>
              </div>
              <div class="user-item-right">
                <div class="success-tips" v-if="user.state === FriendsInfoStatus.getAward">{{ i18n('邀請成功') }}</div>
                <div class="fail-tips" v-else-if="user.state === FriendsInfoStatus.expiredAward">
                  {{ i18n('優惠券已過期') }}
                </div>
                <div class="fail-tips" v-else-if="user.state === FriendsInfoStatus.reachedLimit">
                  {{ i18n('已邀請X位合資格好友', { len: maxInviteCount }) }}
                </div>
                <div class="notice-tips" v-else-if="user.state === FriendsInfoStatus.onWayAward" @click="onShare">
                  {{ i18n('提醒好友') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="invite-list-bottom"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { biClick } from '@tencent/fit-bi-sdk';
import { FriendsInfo, FriendsInfoStatus } from '@tencent/wphk-act-base-cgi';
import { AnnouncementIcon } from '@tencent/wphk-icon-vue';
import { FitButton, NavTabs, NavTabsInfo } from '@tencent/wphk-ui';
import { computed, ref } from 'vue-demi';
import { isEmptyNickName } from '../../business';
import { useActInfo } from '../../hooks/use-act-info';
import { i18n } from '../../i18n';
import { C2CSharerAction, NavTabId } from '../../types';

const emits = defineEmits(['onShare']);

const { phase2FriendsInfo, maxInviteCount, successInviteCount, allCouponDiscount } = useActInfo();

const isFinishInvite = computed(() => successInviteCount.value >= maxInviteCount.value);

function formatNickName(list: FriendsInfo[]): FriendsInfo[] {
  let idx = 0;
  return list
    .slice()
    .reverse()
    .map(item => ({
      ...item,
      nickName: isEmptyNickName(item.nickName) ? i18n('好友') + (idx += 1) : item.nickName,
    }))
    .reverse();
}

function formatFriendsInfoList(obj) {
  return Object.entries(obj).reduce((result, [key, value]) => {
    // eslint-disable-next-line no-param-reassign
    result[key] = formatNickName(value as FriendsInfo[]);
    return result;
  }, {});
}

const friendsInfoList = computed(() => {
  const list: { [key in NavTabId]: FriendsInfo[] } = {
    [NavTabId.success]: [],
    [NavTabId.ing]: [],
    [NavTabId.failed]: [],
  };
  (phase2FriendsInfo.value || []).forEach((element) => {
    if (element.state === FriendsInfoStatus.getAward) {
      list[NavTabId.success].push(element);
    } else if (element.state === FriendsInfoStatus.onWayAward) {
      list[NavTabId.ing].push(element);
    } else {
      list[NavTabId.failed].push(element);
    }
  });
  return formatFriendsInfoList(list);
});

const tabId = ref(NavTabId.success);

const navTabs: NavTabsInfo[] = [
  { name: i18n('已獲獎賞'), id: NavTabId.success },
  { name: i18n('獎賞喺路上'), id: NavTabId.ing },
  { name: i18n('未獲得獎賞'), id: NavTabId.failed },
];

function navChange(tab) {
  biClick('c2c.invite_list_tab', { ftab: tab.id });
  tabId.value = tab.id;
}

function onShare() {
  emits('onShare', C2CSharerAction.share);
}
</script>

<style lang="less"></style>
<style lang="less" scoped>
@import (reference) '~@/less/main';
.title {
  .font-34-medium(#fff);
  text-align: center;
  margin: 16 / @rem 0;
}
.invite-list {
  background-color: #fff;
  border-radius: 24 / @rem;
  padding: 0 / @rem 32 / @rem 0 / @rem 32 / @rem;
  margin-bottom: 16 / @rem;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  .invite-list-tab {
    /deep/ .nav-tabs {
      padding-top: 16 / @rem;
      padding-bottom: 0;
      .tab-wrapper {
        align-items: center;
      }
    }
  }
  .user-list {
    .top-tips {
      margin-top: 12 / @rem;
      display: flex;
      align-items: center;
      background-color: #fff5eb;
      border-radius: 8 / @rem;
      padding: 8 / @rem 16 / @rem;
      .icon {
        width: 32 / @rem;
        height: 32 / @rem;
        min-width: 32 / @rem;
      }
      .text {
        .font-24-regular(@black_90);
        margin-left: 8 / @rem;
      }
    }
    .success-empty {
      display: flex;
      align-items: center;
      flex-direction: column;
      .tips {
        text-align: center;
        .font-28-regular(@black_50);
        margin-bottom: 16 / @rem;
      }
      .btn {
        width: 368 / @rem;
      }
    }
    .user-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 32 / @rem 0;
      border-bottom: 1px solid @black_10;
      .user-item-left {
        display: flex;
        align-items: center;
        .face-url {
          width: 64 / @rem;
          height: 64 / @rem;
          border-radius: 100%;
          margin-right: 16 / @rem;
        }
        .face-url-bg {
          background-color: #c4c4c4;
          opacity: 0.2;
        }
      }
      .user-item-right {
        .success-tips {
          .font-28-medium(@red);
        }
        .fail-tips {
          .font-24-regular(@black_50);
        }
        .notice-tips {
          .font-24-medium(#fff);
          background-color: @green_01;
          padding: 0 16 / @rem;
          border-radius: 24 / @rem;
          line-height: 40 / @rem;
          .en & {
            .font-24-medium(#fff);
            line-height: 40 / @rem;
          }
        }
      }
    }
  }
  .invite-user-list {
    height: 406 / @rem;
    overflow-y: scroll;
    padding-bottom: 68 / @rem;
    .invite-user-list-inner.empty {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .invite-list-bottom {
    width: 100%;
    height: 116 / @rem;
    position: absolute;
    bottom: 0;
    left: 0;
    border-bottom-left-radius: 24 / @rem;
    border-bottom-right-radius: 24 / @rem;
    background: linear-gradient(180deg, transparent, #FFF 100%);
  }
}
</style>
